import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, TextInput, Select } from '@mantine/core'
import useSWR from 'swr'
import ScrollableScreen from '@/components/ui/ScrollableScreen'
import { useAxiosApi } from '@/hooks/useAxiosApi'
import { serviceCaseStatuses, serviceCaseTypes, handlingLocations } from '@/lib/serviceCaseStatuses'

const typeOptions = Object.entries(serviceCaseTypes).map(([value, { label }]) => ({
    value,
    label,
}))

// Statuts disponibles à la création (on exclut finished, refused, cancelled)
const creatableStatuses = [
    'waiting_acceptation',
    'waiting_shipment',
    'received',
    'watchmaker_diagnosis',
    'quote_to_prepare',
    'quote_sent',
    'quote_refused',
    'repairing',
    'quality_control',
    'restocking',
    'to_ship',
]

export default function ServiceCaseCreatePage() {
    const navigate = useNavigate()
    const { requestGet, requestPost } = useAxiosApi()

    const [type, setType] = useState(null)
    const [status, setStatus] = useState(null)
    const [componentId, setComponentId] = useState('accessories')
    const [customerName, setCustomerName] = useState('')
    const [customerEmail, setCustomerEmail] = useState('')
    const [handlingLocation, setHandlingLocation] = useState('baltic_workshop')
    const [isPickupInStore, setIsPickupInStore] = useState(false)
    const [loading, setLoading] = useState(false)

    const { data: refsResponse } = useSWR('/service-cases/product-references', requestGet)
    const productOptions = useMemo(() => {
        const items = [{ value: 'accessories', label: 'Accessoires' }]
        if (refsResponse?.data) items.push(...refsResponse.data)
        return items
    }, [refsResponse])

    // Filtrer les statuts selon le type sélectionné
    const statusOptions = useMemo(() => {
        return creatableStatuses
            .filter((key) => {
                if (!type) return true
                return serviceCaseStatuses[key]?.validTypes?.includes(type)
            })
            .map((key) => ({
                value: key,
                label: serviceCaseStatuses[key].label,
            }))
    }, [type])

    // Reset le statut si le type change et que le statut actuel n'est plus valide
    const handleTypeChange = (value) => {
        setType(value)
        if (status) {
            const validTypes = serviceCaseStatuses[status]?.validTypes
            if (!value || (validTypes && !validTypes.includes(value))) {
                setStatus(null)
            }
        }
    }

    const canSubmit = type && status && customerName && handlingLocation

    const handleSubmit = async () => {
        if (!canSubmit) return
        setLoading(true)
        try {
            const response = await requestPost('/service-cases', {
                type,
                status,
                component_id: componentId === 'accessories' ? null : Number(componentId),
                customer_name: customerName,
                customer_email: customerEmail || null,
                is_handled_by: handlingLocation,
                is_pickup_in_store: isPickupInStore || null,
            })
            if (response?.data?.id) {
                navigate(`/service-cases/${response.data.id}`)
            }
        } finally {
            setLoading(false)
        }
    }

    const breadcrumbs = [
        { label: 'Service après-vente', href: '/service-cases' },
        { label: 'Nouveau SAV' },
    ]

    return (
        <ScrollableScreen breadcrumbs={breadcrumbs}>
            <div className="p-5 mx-auto max-w-[600px]">
                <div className="bg-white rounded-xl border p-6 flex flex-col gap-4">
                    <h2 className="font-medium text-lg">Nouveau SAV</h2>

                    <Select
                        label="Type"
                        placeholder="Sélectionner un type"
                        data={typeOptions}
                        value={type}
                        onChange={handleTypeChange}
                        required
                    />

                    <Select
                        label="Statut initial"
                        placeholder="Sélectionner un statut"
                        data={statusOptions}
                        value={status}
                        onChange={setStatus}
                        disabled={!type}
                        required
                    />

                    <div className="flex flex-col gap-2">
                        <Select
                            label="Lieu de traitement"
                            placeholder="Sélectionner un lieu"
                            data={Object.entries(handlingLocations).filter(([v]) => v !== 'none').map(([value, { label }]) => ({ value, label }))}
                            value={handlingLocation}
                            onChange={setHandlingLocation}
                            required
                        />
                        <div
                            className={`border rounded-xl p-3 cursor-pointer transition-colors flex items-center gap-3 text-sm ${isPickupInStore ? 'border-blue-500 bg-blue-50 text-blue-600' : 'border-gray-200 hover:bg-gray-50'}`}
                            onClick={() => setIsPickupInStore(!isPickupInStore)}
                        >
                            <span className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 text-xs ${isPickupInStore ? 'bg-blue-500 border-blue-500 text-white' : 'border-gray-300'}`}>
                                {isPickupInStore && '✓'}
                            </span>
                            Le client récupérera la montre en boutique
                        </div>
                    </div>

                    <Select
                        label="Produit"
                        placeholder="Rechercher un produit"
                        data={productOptions}
                        value={componentId}
                        onChange={setComponentId}
                        searchable
                        required
                    />

                    <TextInput
                        label="Nom du client"
                        placeholder="Nom complet"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        required
                    />

                    <TextInput
                        label="Email du client"
                        placeholder="email@exemple.com"
                        type="email"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                    />

                    <Button
                        fullWidth
                        mt="sm"
                        onClick={handleSubmit}
                        loading={loading}
                        disabled={!canSubmit}
                    >
                        Créer le SAV
                    </Button>
                </div>
            </div>
        </ScrollableScreen>
    )
}
